/**
 * "Other git server" — any remote URL (git sync Stage 1, card 6 step 3):
 * Gitea, Forgejo, Codeberg, Bitbucket, a bare repo over SSH, a company server.
 * No API: the repository must already exist. Credentials are a PERSONAL Vault
 * item (a Password item: HTTPS user + token; an SFTP item with a private key:
 * SSH). Only the item's id and name ever reach the webview — Rust reads the
 * secret itself (src-tauri/src/git_credentials.rs), the same rule as the
 * Terminal's SSH connections.
 *
 * The link stores { provider: 'remote', url, vaultItemId, vaultItemName }.
 */
const SCP_LIKE = /^[\w.-]+@[\w.-]+:[^/\\].*$/;

/**
 * A remote URL Apiboo will push to: https, ssh:// or user@host:path, or plain
 * http only on this machine (a token must not travel unencrypted).
 */
export function normalizeRemoteUrl(input) {
  const u = String(input || '').trim();
  if (!u) throw new Error('Paste the repository URL.');
  if (SCP_LIKE.test(u)) return u;
  let url;
  try {
    url = new URL(u);
  } catch {
    throw new Error('That does not look like a repository URL.');
  }
  const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  // Every scheme — the local http:// case used to skip this and kept the
  // password in the workspace's git link (found in the Sandbox run, 03.10.2026).
  if (url.password) throw new Error('Keep the password out of the URL — choose a Vault item instead.');
  if (url.protocol === 'ssh:' || url.protocol === 'https:') return u;
  if (url.protocol === 'http:' && local) return u;
  throw new Error('Use an https://, ssh:// or git@host: URL.');
}

/** { owner, repo } from the URL's path, for display ("team/backend" + "api"). */
export function ownerRepoOf(url) {
  const path = SCP_LIKE.test(url) ? url.split(':').slice(1).join(':') : (() => {
    try {
      return new URL(url).pathname;
    } catch {
      return '';
    }
  })();
  const parts = path.replace(/\.git$/, '').split('/').filter(Boolean);
  const repo = parts.pop() || 'repository';
  return { owner: parts.join('/'), repo };
}

const remote = {
  id: 'remote',
  label: 'Other git server',
  icon: 'git',
  authKinds: ['vault'],
  baseUrl: '',
  abilities: { createRepo: false, listRepos: false, openInBrowser: true, deviceFlow: false, tagWorkspace: false },
  /** Credentials come from the Vault in Rust — no token, no user name from here. */
  usesVault: true,
  httpsUsername: undefined,
  notLinkedCode: 'REMOTE_NO_CREDENTIAL',

  async getToken() {
    return '';
  },

  /** "Signed in" = the link names a Vault item. The author stays Apiboo's default. */
  async getAccount(git) {
    return git?.vaultItemId
      ? { connected: true, login: '', avatarUrl: '', label: git.vaultItemName || 'Vault item' }
      : { connected: false, login: '', avatarUrl: '' };
  },

  authorEmail() {
    return 'noreply@apiboo.com';
  },

  async repoUrl(_owner, _repo, git) {
    return git?.url || '';
  },

  /** Only an https remote has a page to open. */
  webUrl(git) {
    return /^https:\/\//i.test(git?.url || '') ? git.url.replace(/\.git$/, '') : '';
  },

  parseRepo(input) {
    try {
      const url = normalizeRemoteUrl(input);
      return { ...ownerRepoOf(url), url };
    } catch {
      return null;
    }
  },
};

export default remote;
